/**
 * @ddd/coaching-export
 *
 * Turns the Knowledge Hub's coaching export into something a person would
 * actually want to keep.
 *
 * WHY THIS EXISTS. The Hub returns raw Firestore: {_seconds,_nanoseconds}
 * timestamps, dotted keys like "apps.dating", nulls throughout. Every app was
 * dumping that straight to a .json file behind a button promising "everything
 * we hold about you" — so a user asking for a record of their conversation got
 * database internals, and until 2026-08-07 the transcripts were not even in the
 * payload. The Hub now attaches them; this renders them.
 *
 * These conversations are often worth keeping — a Profound Living user's
 * transcript may be about a bereavement; a Pemberton one is likely to be funny
 * enough to share. Neither survives being handed over as JSON.
 *
 * Consumed via a Vite alias, matching @ddd/footer-data:
 *   '@ddd/coaching-export': path.resolve(__dirname, '../../packages/coaching-export/src/index.js')
 *
 * Australian English throughout.
 */

/** Firestore timestamps arrive as { _seconds, _nanoseconds }. */
export function readableDate(ts, locale = 'en-AU') {
  const ms = toMillis(ts);
  if (ms === null) return null;
  return new Date(ms).toLocaleString(locale, {
    day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Profiles store the country as an ISO-2 code, which reads as noise in a
 * document meant for a person ("I'm based in **AU**"). Only the seven codes the
 * coaches offer are mapped; anything else falls through unchanged.
 */
const COUNTRY_LABELS = {
  AU: 'Australia',
  NZ: 'New Zealand',
  IN: 'India',
  UK: 'the United Kingdom',
  GB: 'the United Kingdom',
  IE: 'Ireland',
  CA: 'Canada',
  US: 'the United States',
};

export function countryLabel(code) {
  if (!code) return null;
  return COUNTRY_LABELS[String(code).toUpperCase()] || code;
}

/** Coaching sends { _seconds }; support sends an ISO string. Accept both. */
function toMillis(ts) {
  const ms = ts?._seconds ? ts._seconds * 1000 : (ts ? Date.parse(ts) : NaN);
  return Number.isNaN(ms) ? null : ms;
}

/** Day heading, e.g. "Tuesday, 5 August 2026". */
function readableDay(ts, locale = 'en-AU') {
  const ms = toMillis(ts);
  if (ms === null) return null;
  return new Date(ms).toLocaleDateString(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

/** Time only, e.g. "4:05 pm". */
function readableTime(ts, locale = 'en-AU') {
  const ms = toMillis(ts);
  if (ms === null) return null;
  return new Date(ms).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

export function downloadFile(text, filename, mime = 'text/markdown') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ────────────────────────────────────────────────────────────────────────────
 * HTML output
 *
 * Markdown was still a developer format. Double-clicking a .md gives Windows
 * users a "how do you want to open this?" dialog, Mac users TextEdit or Xcode,
 * and phones frequently nothing at all — and whatever opens it shows the
 * asterisks and hashes as literal characters. Someone who has just downloaded a
 * conversation about a bereavement should not have to pick an application.
 *
 * A self-contained HTML file double-clicks straight into a browser on any
 * device, renders styled, and prints to PDF natively — so people get a PDF
 * without us shipping a PDF library to twelve apps.
 *
 * The builders keep emitting Markdown; this renders that known subset. Content
 * is escaped BEFORE any formatting is applied: the transcript contains whatever
 * the user typed, and a shareable .html file that executed it would be a
 * genuine hazard.
 * ──────────────────────────────────────────────────────────────────────────── */

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Inline formatting, applied only AFTER escaping. */
function inlineMarkup(escaped) {
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Underscores only when they wrap a run and sit on word boundaries, so
    // snake_case in a pasted error message survives intact.
    .replace(/(^|\s)_([^_]+)_(?=$|[\s.,;:!?])/g, '$1<em>$2</em>');
}

/**
 * Render the Markdown subset our builders emit: #/##/### headings, "- " lists,
 * **bold**, _italic_, and blank-line-separated paragraphs. Deliberately not a
 * general Markdown parser — anything outside that subset renders as plain text
 * rather than silently doing something surprising.
 */
export function markdownToHtml(md) {
  const lines = String(md).split('\n');
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { closeList(); continue; }

    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      closeList();
      const level = h[1].length;
      out.push(`<h${level}>${inlineMarkup(escapeHtml(h[2]))}</h${level}>`);
      continue;
    }

    if (/^-\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inlineMarkup(escapeHtml(line.replace(/^-\s+/, '')))}</li>`);
      continue;
    }

    closeList();
    const html = inlineMarkup(escapeHtml(line));
    // "**Me:** …" and "**Willow:** …" become speech rows so the two voices are
    // visually distinct rather than a wall of bold-prefixed paragraphs.
    const speech = /^<strong>([^<]{1,40}):<\/strong>\s*([\s\S]*)$/.exec(html);
    if (speech) {
      const who = speech[1];
      const isUser = who === 'Me';
      out.push(
        `<div class="turn ${isUser ? 'me' : 'coach'}">` +
        `<span class="who">${who}</span>` +
        `<div class="said">${speech[2]}</div></div>`,
      );
      continue;
    }
    out.push(`<p>${html}</p>`);
  }
  closeList();
  return out.join('\n');
}

/** Wrap rendered content in a standalone, printable document. */
export function htmlDocument(bodyHtml, { title = 'My conversations' } = {}) {
  return `<!doctype html>
<html lang="en-AU">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
  :root { color-scheme: light; }
  body {
    margin: 0; padding: 2.5rem 1.25rem 4rem;
    background: #f6f4f1; color: #24211e;
    font: 16px/1.65 Georgia, 'Iowan Old Style', 'Times New Roman', serif;
  }
  main { max-width: 38rem; margin: 0 auto; }
  h1 { font-size: 1.8rem; line-height: 1.25; margin: 0 0 .35rem; }
  h2 { font-size: 1.25rem; margin: 2.5rem 0 .75rem; padding-bottom: .35rem;
       border-bottom: 1px solid #ddd7cf; }
  h3 { font-size: 1.02rem; margin: 2rem 0 .5rem; color: #5c534a; }
  p { margin: 0 0 .9rem; }
  ul { margin: 0 0 1rem; padding-left: 1.2rem; }
  li { margin: .3rem 0; }
  em { color: #6b6157; }
  .turn { margin: 0 0 1.1rem; }
  .who { display: block; font-size: .78rem; letter-spacing: .06em;
         text-transform: uppercase; color: #8a7f73; margin-bottom: .2rem; }
  .said { padding: .7rem .9rem; border-radius: .6rem; }
  .me .said    { background: #e9e4dd; }
  .coach .said { background: #fff; border: 1px solid #e4ded6; }
  @media print {
    body { background: #fff; padding: 0; font-size: 12pt; }
    h2 { page-break-after: avoid; }
    .turn { page-break-inside: avoid; }
    .coach .said { border-color: #ccc; }
  }
</style>
</head>
<body><main>
${bodyHtml}
</main></body>
</html>`;
}

/** Markdown in, standalone printable document out. */
export function buildHtmlTranscript(markdown, { title } = {}) {
  return htmlDocument(markdownToHtml(markdown), { title });
}

/**
 * @param {Object} payload   - the raw { success, data, truncated } from exportCoachingData
 * @param {Object} opts
 * @param {string} opts.coachName - "Willow", "Sparky", "Pemberton"…
 * @param {string} opts.appId     - filters sessions to this app
 * @param {string} [opts.appName] - for the header line
 * @param {string} [opts.subject] - pronoun used for the coach; defaults to "they"
 * @returns {string} Markdown
 */
export function buildReadableTranscript(payload, { coachName, appId, appName, subject = 'they' } = {}) {
  const d = payload?.data || {};
  const p = d.profile || {};
  const pc = p.personalContext || {};
  const out = [];
  const Coach = coachName || 'your coach';
  // Possessive/subject forms so this reads naturally whichever coach it is.
  const subj = subject;
  const does = subj === 'they' ? 'do' : 'does';

  out.push(`# My conversations with ${Coach}`);
  out.push('');
  out.push(`Downloaded ${new Date().toLocaleString('en-AU')}${appName ? ` from ${appName}` : ''}.`);
  out.push('');
  out.push(`## What ${Coach} remembers about me`);
  out.push('');
  if (pc.firstName) out.push(`- ${Coach} calls me **${pc.firstName}**`);
  if (pc.country) out.push(`- I'm based in **${countryLabel(pc.country)}** (so the right support services are shown)`);
  if (pc.preferredCoachingStyle) out.push(`- I asked for a **${pc.preferredCoachingStyle}** style`);
  out.push(`- Sharing across Dive Deep Dialogues apps: **${p.consent?.dataSharingScope === 'cross-app' ? 'on' : 'this app only'}**`);
  const granted = readableDate(p.consent?.grantedAt);
  if (granted) out.push(`- I agreed to this on ${granted}`);
  if (p.profileSummary) { out.push(''); out.push(p.profileSummary); }

  const app = p[`apps.${appId}`] || p.apps?.[appId] || {};
  const goals = (app.activeGoals || []).filter(Boolean);
  if (goals.length) {
    out.push('');
    out.push('### Goals I set');
    goals.forEach((g) => out.push(`- ${g.description || g}`));
  }
  const struggles = (app.knownStruggles || []).filter(Boolean);
  if (struggles.length) {
    out.push('');
    out.push('### Things I said I was finding hard');
    struggles.forEach((s) => out.push(`- ${s.topic || s}`));
  }

  const promoted = (p.professionalSupportPromoted || []).filter(Boolean);
  if (promoted.length) {
    out.push('');
    out.push(`### Times ${Coach} suggested professional support`);
    promoted.forEach((s) => {
      const when = readableDate(s.suggestedAt);
      out.push(`- ${s.suggestedType || 'support'}${s.topic ? ` — about ${s.topic}` : ''}${when ? ` (${when})` : ''}`);
    });
  }

  const sessions = (d.sessions || [])
    .filter((s) => !appId || s.appId === appId)
    .sort((a, b) => (a.startedAt?._seconds || 0) - (b.startedAt?._seconds || 0));

  out.push('');
  out.push('## Our conversations');
  if (!sessions.length) out.push('', '_No conversations yet._');

  sessions.forEach((s, i) => {
    const when = readableDate(s.startedAt);
    out.push('');
    out.push(`### Conversation ${i + 1}${when ? ` — ${when}` : ''}`);
    if (s.summary?.topic) out.push(`_${s.summary.topic}_`);
    out.push('');
    if (Array.isArray(s.messages) && s.messages.length) {
      s.messages.forEach((m) => {
        out.push(`**${m.role === 'user' ? 'Me' : Coach}:** ${m.content}`);
        out.push('');
      });
    } else if (s.messagesUnavailable) {
      out.push('_This conversation could not be retrieved._');
    } else {
      out.push('_No messages were recorded in this conversation._');
    }
    const insights = s.summary?.keyInsights || [];
    if (insights.length) {
      out.push(`**What ${subj} took from it:**`);
      insights.forEach((k) => out.push(`- ${k}`));
      out.push('');
    }
    const commitments = s.summary?.commitmentsMade || [];
    if (commitments.length) {
      out.push('**What I said I would do:**');
      commitments.forEach((c) => out.push(`- ${c}`));
      out.push('');
    }
  });

  // Never let a truncated export pass itself off as the whole record.
  if (payload?.truncated?.sessionsOmitted) {
    out.push('');
    out.push(`_Note: ${payload.truncated.sessionsOmitted} older conversations were not included in this file._`);
  }
  return out.join('\n');
}

/** Convenience: build and download in one call. */
export function downloadReadableTranscript(payload, opts = {}) {
  const slug = (opts.coachName || 'coach').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const title = `My conversations with ${opts.coachName || 'my coach'}`;
  downloadFile(
    buildHtmlTranscript(buildReadableTranscript(payload, opts), { title }),
    `my-conversations-with-${slug}.html`,
    'text/html',
  );
}

/* ------------------------------------------------------------------ *
 * SUPPORT PATH
 *
 * Pemberton and the other in-app helpers never touch exportCoachingData.
 * Their conversations come back from getSupportHistory in a flatter shape —
 * one document per EXCHANGE (a question and its answer), newest first, with
 * no profile, no sessions and no summaries:
 *
 *   { success, conversations: [{ id, appId, userMessage, assistantResponse,
 *                                timestamp: ISO string, ... }] }
 *
 * Same promise to the user, different plumbing, so it gets its own builder
 * rather than a shape-shifting flag on the coaching one.
 * ------------------------------------------------------------------ */

/** Oldest-first, app-filtered, only exchanges that actually said something. */
function orderedExchanges(payload, appId) {
  return (payload?.conversations || [])
    .filter((c) => c && (c.userMessage || c.assistantResponse))
    .filter((c) => !appId || !c.appId || c.appId === appId)
    .slice()
    .sort((a, b) => (toMillis(a.timestamp) || 0) - (toMillis(b.timestamp) || 0));
}

/**
 * @param {Object} payload      - the raw { success, conversations } from getSupportHistory
 * @param {Object} opts
 * @param {string} opts.coachName  - "Pemberton"…
 * @param {string} [opts.appId]    - belt-and-braces filter; the Hub already scopes by app
 * @param {string} [opts.appName]  - for the header line
 * @param {number} [opts.limit]    - the limit that was requested, so a capped
 *                                   history can say so instead of passing as complete
 * @param {Array<{label: string, value: any}>} [opts.progress]
 *                                 - optional "how far I've got" rows; these apps
 *                                   promise progress in the same export
 * @returns {string} Markdown
 */
export function buildReadableSupportTranscript(
  payload,
  { coachName, appId, appName, limit, progress } = {},
) {
  const Coach = coachName || 'your assistant';
  const out = [];

  out.push(`# My conversations with ${Coach}`);
  out.push('');
  out.push(`Downloaded ${new Date().toLocaleString('en-AU')}${appName ? ` from ${appName}` : ''}.`);

  const rows = (progress || []).filter((r) => r && r.label && r.value !== undefined && r.value !== null);
  if (rows.length) {
    out.push('');
    out.push('## How far I have got');
    out.push('');
    rows.forEach((r) => out.push(`- ${r.label}: **${r.value}**`));
  }

  const exchanges = orderedExchanges(payload, appId);

  out.push('');
  out.push('## Our conversations');
  if (!exchanges.length) {
    out.push('');
    out.push(`_You haven't asked ${Coach} anything yet._`);
    return out.join('\n');
  }

  // One heading per day rather than per exchange — a support history is many
  // short questions, and a heading above each would bury the answers.
  let currentDay = null;
  exchanges.forEach((c) => {
    const day = readableDay(c.timestamp);
    if (day !== currentDay) {
      currentDay = day;
      out.push('');
      out.push(`### ${day || 'Undated'}`);
    }
    const time = readableTime(c.timestamp);
    out.push('');
    if (time) out.push(`_${time}_`);
    out.push('');
    if (c.userMessage) { out.push(`**Me:** ${c.userMessage}`); out.push(''); }
    if (c.assistantResponse) { out.push(`**${Coach}:** ${c.assistantResponse}`); out.push(''); }
  });

  // getSupportHistory takes a limit and gives no total, so a full page of
  // results may well be the top of a longer history. Say so rather than let
  // the file imply it is everything.
  if (limit && exchanges.length >= limit) {
    out.push('');
    out.push(`_Note: this file holds your ${exchanges.length} most recent exchanges. Anything older is not included._`);
  }
  return out.join('\n');
}

/** Convenience: build and download in one call. */
export function downloadReadableSupportTranscript(payload, opts = {}) {
  const slug = (opts.coachName || 'assistant').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const title = `My conversations with ${opts.coachName || 'my assistant'}`;
  downloadFile(
    buildHtmlTranscript(buildReadableSupportTranscript(payload, opts), { title }),
    `my-conversations-with-${slug}.html`,
    'text/html',
  );
}

/**
 * One exchange as plain text, ready to paste anywhere.
 *
 * The support coaches say quotable things and there is no privacy reason to
 * keep them in a file — so a single reply should be one tap from the clipboard,
 * carrying its attribution with it.
 *
 * @param {Object} exchange - a single getSupportHistory conversation document
 * @param {Object} opts
 * @param {string} opts.coachName
 * @param {string} [opts.appName] - named alongside the coach, so a stranger
 *                                  reading the paste knows where he came from
 * @param {string} [opts.siteUrl] - trailing credit line
 */
export function formatExchangeForSharing(exchange, { coachName, appName, siteUrl } = {}) {
  const Coach = coachName || 'your assistant';
  const parts = [];
  if (exchange?.userMessage) parts.push(`Q: ${exchange.userMessage}`, '');
  if (exchange?.assistantResponse) parts.push(`${Coach}: ${exchange.assistantResponse}`, '');
  parts.push(`— ${Coach}${appName ? `, ${appName}` : ''}`);
  if (siteUrl) parts.push(siteUrl);
  return parts.join('\n');
}

/**
 * Clipboard write that survives the places navigator.clipboard is not there —
 * insecure origins, older in-app browsers, an iOS webview without permission.
 * @returns {Promise<boolean>} whether the text actually made it across
 */
export async function copyTextToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Convenience: format one exchange and copy it. */
export function copyExchangeToClipboard(exchange, opts = {}) {
  return copyTextToClipboard(formatExchangeForSharing(exchange, opts));
}
